import createFetchClient from 'openapi-fetch';
import createQueryClient from 'openapi-react-query';
import type { paths as AePaths } from '../../types/apis/ae/schema-typescript';
import type { paths as DmsmPaths } from '../../types/apis/dmsm/schema-typescript';
import type { paths as SamPaths } from '../../types/apis/sam/schema-typescript';
import type { paths as SdiCertPaths } from '../../types/apis/sdiCert/schema-typescript';
import type { paths as SepPaths } from '../../types/apis/sep/schema-typescript';
import type { paths as SroPaths } from '../../types/apis/sro/schema-typescript';
import type { paths as SupplierGroupPaths } from '../../types/apis/supplierGroup/schema-typescript';
import type { paths as SupplierLocationPaths } from '../../types/apis/supplierLocation/schema-typescript';
import { aeOperations } from './ae/operations';
import { dmsmOperations } from './dmsm/operations';
import { riskRatingOperations } from './riskRating/operations';
import { samOperations } from './sam/operations';
import { sdiCertOperations } from './sdiCert/operations';
import { sepOperations } from './sep/operations';
import { sroOperations } from './sro/operations';
import { supplierGroupOperations } from './supplierGroup/operations';
import { supplierLocationOperations } from './supplierLocation/operations';
import { getEnvVar } from '../getEnvVar';

import { oktaAuth } from '../../oktaAuth';

/**
 * Creates an openapi-fetch client with an auth middleware that attaches the
 */

// Devportal links for relevant APIs:
// SRO - https://devportal.deere.com/catalog/default/api/supplier-relationship-owner/
// SGA - https://devportal.deere.com/catalog/default/api/supplier-group-api/definition
// riskRating - https://devportal.deere.com/catalog/default/api/scs-rcs-resiliency-hub-risk-ratings-api-prod/definition
// AE - https://devportal.deere.com/catalog/default/api/ae-api-prod/definition
// sdi-cert - https://devportal.deere.com/catalog/default/api/cert-mngt-api-prod/definition
// supplierLocation - https://devportal.deere.com/catalog/default/api/supplier-location-api-prod/definition
// SAM - https://devportal.deere.com/catalog/default/api/sam-api-v2

function createAuthenticatedClient<TPaths extends {}>(baseUrl: string) {
  const client = createFetchClient<TPaths>({ baseUrl });
  client.use({
    onRequest({ request }) {
      const token = oktaAuth.getAccessToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      return request;
    }
  });
  return client;
}

function createApi<TPaths extends {}, TOperations>(
  baseUrl: string,
  operations: TOperations
) {
  const fetchClient = createAuthenticatedClient<TPaths>(baseUrl);
  return {
    fetchClient,
    queryClient: createQueryClient(fetchClient),
    operations
  };
}

export const aeApi = createApi<AePaths, typeof aeOperations>(
  getEnvVar('VITE_SUPPLIERS_AE_RATING_API_URL'),
  aeOperations
);

export const dmsmApi = createApi<DmsmPaths, typeof dmsmOperations>(
  getEnvVar('VITE_DMSM_API_URL'),
  dmsmOperations
);

// RiskRatingPaths,
// typeof riskRatingOperations
// spec was incorrect, fill with any for now

export const riskRatingApi = createApi<any, any>(
  getEnvVar('VITE_SUPPLIERS_RISK_RATING_API_URL'),
  riskRatingOperations
);
export const samApi = createApi<SamPaths, typeof samOperations>(
  getEnvVar('VITE_SAM_API_URL'),
  samOperations
);
export const sdiCertApi = createApi<SdiCertPaths, typeof sdiCertOperations>(
  getEnvVar('VITE_SDI_CERT_API_URL'),
  sdiCertOperations
);
export const sepApi = createApi<SepPaths, typeof sepOperations>(
  getEnvVar('VITE_SEP_API_URL'),
  sepOperations
);
export const sroApi = createApi<SroPaths, typeof sroOperations>(
  getEnvVar('VITE_SRO_API_URL'),
  sroOperations
);
export const supplierGroupApi = createApi<
  SupplierGroupPaths,
  typeof supplierGroupOperations
>(getEnvVar('VITE_SUPPLIER_GROUPS_API_URL'), supplierGroupOperations);
export const supplierLocationApi = createApi<
  SupplierLocationPaths,
  typeof supplierLocationOperations
>(getEnvVar('VITE_SUPPLIER_LOCATION_API_URL'), supplierLocationOperations);
